import { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { errorMessage } from '../utils/utils';

/**
 * Credits are a peso balance: customers top them up by e-wallet (at least
 * ₱100, so Xendit's per-transaction fee is paid once per top-up) and spend
 * them on remote accounts. Keep these in step with credits/credits.ts in the API.
 */
export const TOPUP_MINIMUM = 100;
export const TOPUP_MAXIMUM = 10000;

export function peso(amount?: number) {
    const value = amount ?? 0;
    return `${value < 0 ? '-' : ''}₱${Math.abs(value).toLocaleString()}`;
}

/** A change to the balance: "+₱100" or "-₱50". */
export function signedPeso(amount?: number) {
    return `${(amount ?? 0) > 0 ? '+' : ''}${peso(amount)}`;
}

/**
 * The signed-in user's credit balance. It reloads whenever the tab regains
 * focus, so a top-up finished in another tab shows up without a page refresh.
 */
export function useCredits() {
    const [balance, setBalance] = useState<number>();
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        try {
            const res = await api.get('/credits');
            const json = await res.json();
            if (res.ok) {
                setBalance(json.balance);
                setError('');
            } else {
                setError(errorMessage(json));
            }
        } catch (err) {
            setError(errorMessage(err));
        }
    }, []);

    useEffect(() => {
        reload();
        window.addEventListener('focus', reload);
        return () => window.removeEventListener('focus', reload);
    }, [reload]);

    return { balance, error, reload };
}

/** What a purchase or renewal costing `price` does to the balance, with a top-up link when it falls short. */
export function CreditsPayment({ price, balance, error }: { price: number, balance?: number, error?: string }) {

    const loaded = balance !== undefined;
    const shortfall = loaded ? price - balance : 0;

    return (
        <div>
            <div className='flex items-start justify-between gap-4 py-1.5 text-sm'>
                <span className='text-slate-500'>Price</span><span className='text-right font-semibold text-slate-800'>{peso(price)}</span>
            </div>

            <div className='flex items-start justify-between gap-4 py-1.5 text-sm'>
                <span className='text-slate-500'>Your credits</span><span className='text-right font-semibold text-slate-800'>{loaded ? peso(balance) : '—'}</span>
            </div>

            <div className='mt-2 flex items-center justify-between border-t border-slate-100 pt-3'>
                <span className='text-sm font-bold text-slate-700'>Credits after</span>
                <span className={`text-xl font-bold ${shortfall > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{loaded ? peso(balance - price) : '—'}</span>
            </div>

            {shortfall > 0 && (
                <div className='mt-3 rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800 ring-1 ring-amber-200'>
                    You need <b>{peso(shortfall)}</b> more credits.{' '}
                    <a href={`/credits?amount=${Math.max(TOPUP_MINIMUM, shortfall)}`} target='_blank' rel='noreferrer' className='font-bold underline'>Top up credits</a>
                    {' '}in a new tab. Your balance updates here when you come back.
                </div>
            )}

            {error && <div className='mt-2 text-xs text-red-500' dangerouslySetInnerHTML={{ __html: error }} />}
        </div>
    );
}
